// SectionPanel — collapsible panel for one of the LLM-generated sections
// (Outline / Hook / Draft) on the script editor route.
//
// Renders a header bar with collapse toggle + copy + regen buttons, then a
// monospaced body for the section text. While `loading` is true the body
// shows a pulsing skeleton so the operator sees per-section progress instead
// of a global spinner.

import { useState } from 'react'
import { Check, ChevronDown, ChevronRight, Copy, RefreshCcw } from 'lucide-react'
import { Button, Card } from '@/admin-design/_common'

interface SectionPanelProps {
  title: string
  /** Section text, e.g. the outline bullets or the draft script. */
  body: string
  /** True while a regen mutation for THIS section is in flight. */
  loading?: boolean
  /** Disabled when the parent doesn't yet have a payload to regenerate from. */
  canRegenerate?: boolean
  onRegenerate?: () => void
  /** Optional. When undefined the copy button is hidden. */
  onCopy?: () => Promise<void> | void
  /** Bytes-rendered placeholder when body is empty. */
  emptyPlaceholder?: string
}

export default function SectionPanel({
  title,
  body,
  loading = false,
  canRegenerate = true,
  onRegenerate,
  onCopy,
  emptyPlaceholder = '(no content yet)',
}: SectionPanelProps) {
  const [open, setOpen] = useState(true)
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!onCopy) return
    try {
      await onCopy()
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // swallow — the copy fallback toast is the parent's responsibility
    }
  }

  return (
    <Card>
      <header className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 text-sm font-semibold text-card-foreground hover:text-info"
          aria-label={open ? 'collapse' : 'expand'}
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {title}
        </button>
        <span className="text-[11px] text-muted-foreground">
          {body ? `${body.length} chars` : 'empty'}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {onCopy && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleCopy}
              disabled={!body}
              className="h-7 px-2 text-xs"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          )}
          {onRegenerate && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={onRegenerate}
              disabled={!canRegenerate}
              loading={loading}
              className="h-7 px-2 text-xs"
            >
              {!loading && <RefreshCcw size={12} />}
              {loading ? 'Regenerating…' : 'Regenerate'}
            </Button>
          )}
        </div>
      </header>
      {open && (
        <div className="p-3">
          {loading ? (
            <SkeletonBody />
          ) : body ? (
            <pre className="whitespace-pre-wrap font-mono text-[13px] leading-relaxed text-card-foreground">
              {body}
            </pre>
          ) : (
            <p className="text-xs italic text-muted-foreground">{emptyPlaceholder}</p>
          )}
        </div>
      )}
    </Card>
  )
}

// Three pulsing bars are enough to communicate "work in flight" without
// occupying the full draft height — the operator's eyes already know which
// section they regenerated.
function SkeletonBody() {
  return (
    <div className="space-y-2 animate-pulse">
      <div className="h-3 rounded bg-muted w-11/12" />
      <div className="h-3 rounded bg-muted w-9/12" />
      <div className="h-3 rounded bg-muted w-10/12" />
    </div>
  )
}
